import axios from 'axios';
import { SellerGroup, HistoryEntry, ProfileChange } from './types';

const client = axios;

type FetchResponse = {
  success: boolean;
  data: {
    success: boolean;
    columns: string[];
    data: (string | null)[][];
  };
};

export async function fetchSellerGroups(): Promise<SellerGroup[]> {
  const { data } = await client.get<FetchResponse>('/seller-group');
  const rows = data?.data?.data ?? [];
  return rows.map((row) => ({
    id: String(row[0] ?? ''),
    name: String(row[1] ?? ''),
    profile: row[2] ?? '',
  }));
}

type UpdateResponse = {
  success: boolean;
  error?: string;
};

export async function updateSellerGroupProfile(
  grupoId: string,
  profile: string
): Promise<UpdateResponse> {
  const { data } = await client.post<UpdateResponse>(
    `/seller-group/unique/${grupoId}`,
    { profile }
  );
  return data;
}

type BatchResultItem = {
  index: number;
  ok: boolean;
  group_id: string | null;
  error?: string;
};

type BatchResponse = {
  success: boolean;
  count?: number;
  results?: BatchResultItem[];
  error?: string;
};

export async function batchUpdateSellerGroups(
  items: { group_id: string; profile: string }[]
): Promise<BatchResponse> {
  const { data } = await client.post<BatchResponse>(
    '/seller-group/list',
    { items }
  );
  return data;
}

type HistoryApiResponse = {
  success: boolean;
  data: {
    success: boolean;
    columns: string[];
    data: (string | null)[][];
  };
};

type ChangeItem = {
  group_id: string;
  before: { profile?: string } | null;
  after: { profile?: string } | null;
};

type ParsedData = {
  type?: string;
  action?: string;
  changes?: ChangeItem[];
  before?: { profile?: string } | null;
  after?: { profile?: string } | null;
};

function extractChanges(
  parsed: ParsedData,
  fallbackGroupId: string,
  fallbackGroupName: string,
  groups: Map<string, string>
): ProfileChange[] {
  if (Array.isArray(parsed.changes)) {
    return parsed.changes.map((c) => ({
      groupId: String(c.group_id ?? fallbackGroupId),
      groupName: groups.get(String(c.group_id)) ?? String(c.group_id),
      oldProfile: c.before?.profile ?? '(nenhum)',
      newProfile: c.after?.profile ?? '',
    }));
  }

  return [{
    groupId: fallbackGroupId,
    groupName: fallbackGroupName,
    oldProfile: parsed.before?.profile ?? '(nenhum)',
    newProfile: parsed.after?.profile ?? '',
  }];
}

export async function fetchSellerGroupHistory(): Promise<HistoryEntry[]> {
  const [historyRes, groupsRes] = await Promise.all([
    client.get<HistoryApiResponse>('/seller-group/history'),
    client.get<FetchResponse>('/seller-group').catch(() => null),
  ]);

  const groupsMap = new Map<string, string>();
  if (groupsRes?.data?.data?.data) {
    for (const row of groupsRes.data.data.data) {
      groupsMap.set(String(row[0] ?? ''), String(row[1] ?? ''));
    }
  }

  const rows = historyRes.data?.data?.data ?? [];

  const groupedMap = new Map<string, {
    date: string;
    userName: string;
    changes: ProfileChange[];
  }>();

  for (const row of rows) {
    const userName = String(row[3] ?? 'Desconhecido');
    const fallbackGroupId = String(row[5] ?? '');
    const fallbackGroupName = String(row[6] ?? '');
    const rawData = String(row[7] ?? '{}');
    const createdAt = String(row[8] ?? '');

    let parsed: ParsedData = {};
    try {
      parsed = JSON.parse(rawData);
    } catch {
      continue;
    }

    const changes = extractChanges(parsed, fallbackGroupId, fallbackGroupName, groupsMap);
    const groupKey = `${createdAt}__${userName}`;

    const existing = groupedMap.get(groupKey);
    if (existing) {
      existing.changes.push(...changes);
    } else {
      groupedMap.set(groupKey, {
        date: createdAt,
        userName,
        changes,
      });
    }
  }

  const entries: HistoryEntry[] = [];
  let idx = 0;
  for (const [, value] of groupedMap) {
    entries.push({
      id: `history-${idx++}`,
      date: value.date,
      user: value.userName,
      changes: value.changes,
    });
  }

  return entries;
}
